import type { Strings } from '../index.ts';
import { unreadable } from './shared.ts';

/**
 * Die Abo-Seite.
 *
 * Der `unseen`-Satz ist der, der sorgfältig geschrieben werden musste. Das Naheliegende — „30 neue
 * Videos“ — ist in beide Richtungen falsch: die Markierung liegt bis zu siebzehn Tage vor dem letzten
 * Besuch, und der einzige neueste Upload im Feed trug sie nicht. Der Satz sagt deshalb, was die
 * Markierung *bedeutet*, und hört dann auf.
 *
 * `list.following` heißt „${count} abonniert“ und nicht „Du folgst ${count}“: es ist die Überschrift über
 * einer Kachelliste und muss so kurz sein wie das englische „Following 12“.
 */
export const subscriptions: Strings['subscriptions'] = {
  notice: {
    noSubscriptions:
      'Dieses Konto folgt noch keinen Kanälen, der Feed hat also nichts, woraus er sich bauen könnte. Öffne einen Kanal und drücke Abonnieren.',
  },

  button: {
    subscribe: 'Abonnieren',
    unsubscribe: 'Abo beenden',
    signedOutTitle:
      'Melde dich bei deiner Instanz an, um zu abonnieren, und du kommst direkt hierher zurück',
    invalidChannel: {
      title: 'Abonnieren nicht möglich',
      detail: 'Die Instanz sagt, dieser Kanal existiert nicht oder wurde gelöscht.',
      retryable: false,
    },
    failed: {
      title: 'Das Abo ließ sich nicht ändern',
      detail:
        'Die Instanz hat die Änderung nicht angenommen. Es wurde nichts verändert, ein neuer Versuch ist also unbedenklich.',
      retryable: true,
    },
  },

  unseen: {
    label: 'Ungesehen',
    title:
      'Von deiner Instanz als ungesehen markiert. Die Markierung erscheint, wenn ein Kanal etwas veröffentlicht, und verschwindet, wenn du deine Abos auf der Instanz selbst öffnest. Sie bedeutet also nicht „neu seit deinem letzten Besuch hier“.',
  },

  feed: {
    allDropped: 'Die Instanz hat geantwortet, und nichts darin ließ sich anzeigen.',
    allDroppedTitle: 'Dieser Feed ließ sich nicht lesen',
    emptyNoChannels: {
      title: 'Du folgst noch keinen Kanälen',
      detail:
        'Der Feed entsteht aus deinen Abos auf der Instanz, er bleibt also leer, bis es etwas gibt, woraus er entstehen kann.',
    },
    emptyNoVideos: {
      title: 'Noch nichts im Feed',
      detail:
        'Deine Abos sind da, aber es gibt nichts im Feed dafür. Die Instanz baut ihn nach einem Zeitplan, ein gerade hinzugefügter Kanal kann also eine Weile brauchen, bis er auftaucht.',
    },
  },

  page: {
    heading: 'Abos',
    lede: 'Was die Kanäle in deinem Instanz-Konto veröffentlicht haben, das Neueste zuerst.',
    loading: 'Dein Abo-Feed wird geladen…',
    gridLabel: 'Abo-Feed',
    loadMore: (soFar) => `Mehr laden (bisher ${soFar})`,
    loadingMore: 'Wird geladen…',
    manage: 'Verwalten',
    watched: {
      label: 'Gesehene',
      shown: 'Gezeigt',
      hidden: 'Versteckt',
    },
  },

  manage: {
    heading: 'Abos verwalten',
    lede: 'Kanäle hinzufügen, oder ein Abo mit dem Knopf auf der Kachel beenden.',
    done: 'Fertig',
    add: {
      heading: 'Abo hinzufügen',
      label: 'Kanal',
      placeholder: 'Eine Kanal-URL, ein @handle, eine Kanal-ID oder ein Name zum Suchen',
      clear: 'Feld leeren',
      submit: 'Finden',
      searching: 'Wird gesucht…',
      resultsLabel: 'Gefundene Kanäle',
      nothing: (q) => `Nichts gefunden für „${q}“.`,
      notResolvable: 'Dieser Link benennt keinen Kanal.',
      autoGenerated: 'Automatisch erstellt',
    },
    list: {
      following: (count) => `${count} abonniert`,
      loading: 'Deine Abos werden geladen…',
      empty: 'Noch keine Abos. Füge oben eines hinzu.',
      filterLabel: 'Deine Abos filtern',
      filterPlaceholder: 'Nach Namen filtern',
      filterClear: 'Filter löschen',
      filterActiveTitle: (q) => `Gefiltert nach „${q}“`,
      noMatches: (q) => `Kein Abo passt zu „${q}“.`,
      sort: {
        label: 'Sortierung',
        listOrder: 'Listenreihenfolge',
        nameAsc: 'Name A–Z',
        nameDesc: 'Name Z–A',
      },
      filterDone: 'Filtern beenden',
      subscribers: (count) => `${count} Abonnenten`,
      gone: 'Gelöscht oder gesperrt',
      goneTitle: (name) =>
        `YouTube liefert ${name} nicht mehr. Der Kanal wurde gelöscht oder gesperrt, und das Abo zu beenden ist die einzige Änderung, die hier noch bleibt.`,
      unsubscribeOne: (name) => `Abo von ${name} beenden`,
      removing: 'Abo wird beendet…',
      removeFailed: {
        title: 'Das Abo ließ sich nicht beenden',
        detail:
          'Die Instanz hat die Änderung abgelehnt. Die Liste unten zeigt, was sie jetzt hält, und ein neuer Versuch ist unbedenklich.',
        retryable: true,
      },
    },
  },

  problem: {
    feedNotFound: {
      title: 'Diese Instanz hat keinen Abo-Feed',
      detail: 'Er wird hier nicht angeboten. Manche Instanzen lassen Teile der App weg.',
      retryable: false,
    },
    feedUnreadable: unreadable('Der Feed ließ sich nicht lesen'),
    feedDisabled: {
      title: 'Der Abo-Feed ist auf dieser Instanz abgeschaltet',
      detail:
        'Die Administration hat ihn abgeschaltet. Deine Abos selbst sind davon unberührt, und die Liste unten funktioniert weiter.',
      retryable: false,
    },
    listNotFound: {
      title: 'Diese Instanz hat keine Abo-Liste',
      detail: 'Die Kanäle, denen du folgst, lassen sich hier nicht lesen.',
      retryable: false,
    },
    listUnreadable: unreadable('Deine Abos ließen sich nicht lesen'),
  },
};
